import moment from "moment";
import { claveFechaDia } from "../Components/fmtFechaDia";

/**
 * Pdf/borradorAguinaldoPdf.js
 *
 * Hoja de trabajo del aguinaldo: el borrador de un proceso de "Generar Aguinaldo" con el ISR que
 * calculó el motor (simulación sin guardar), o el aguinaldo ya aplicado con los importes reales de
 * cada recibo. Los datos los arma el endpoint nomina/datosImpresionAguinaldo; aquí sólo se maqueta.
 *
 * Dos partes:
 *  1. Detalle por prenómina destino, con renglones compactos (≈45 empleados por hoja horizontal,
 *     así que 100 o más caben en pocas hojas). El encabezado de la tabla se repite en cada hoja y
 *     un renglón nunca se parte entre dos hojas. Con `conFirma` se agrega la columna de firma y los
 *     renglones crecen lo necesario para poder firmar.
 *  2. Hoja de totales en una página aparte: datos del proceso, totales por destino, total general
 *     y espacio para firmas de elaboró / revisó / autorizó.
 *
 * Trampas de pdfmake cubiertas (ver memoria del proyecto): ningún `body` queda vacío, cada tabla
 * recibe su propio arreglo de `widths` (pdfmake lo muta), las celdas vacías son objetos distintos y
 * toda fila sale de la MISMA lista de columnas, así que siempre tiene tantas celdas como `widths`.
 *
 * Paleta de marca BIWO, igual que nominaNoTimbradosPdf y la Lista de Raya.
 */

const C_TURQUESA = "#00BFD6";
const C_NARANJA = "#FF6A1A";
const C_TEAL_PRO = "#005F6B";
const C_BANDA_GR = "#BFEDF5";
const C_ZEBRA = "#F5FBFC";
const C_TINTA_SU = "#8C8C8C";
const C_BORDE = "#D9D9D9";

const ANCHO_UTIL = 736; // LETTER horizontal (792) menos márgenes de 28

const fmtDinero = (n) =>
  n === null || n === undefined
    ? "—"
    : (Number(n) || 0).toLocaleString("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 });

const fmtDias = (n) =>
  (Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 4 });

const fechaTxt = (f) => {
  const k = claveFechaDia(f);
  return k ? k.split("-").reverse().join("/") : "";
};

const codigoTxt = (c) => (c === null || c === undefined || c === "" ? "" : String(c).padStart(4, "0"));

const redondear = (n, d = 2) => Math.round((Number(n) || 0) * 10 ** d) / 10 ** d;

// Suma de un campo; `null` (ISR sin calcular) cuenta como 0, igual que el backend.
const sumar = (filas, campo, d = 2) => redondear(filas.reduce((acc, f) => acc + (Number(f[campo]) || 0), 0), d);

const totalesDe = (filas) => ({
  empleados: filas.length,
  diasProporcionales: sumar(filas, "diasProporcionales", 4),
  importe: sumar(filas, "importe"),
  exento: sumar(filas, "exento"),
  gravado: sumar(filas, "gravado"),
  isr: sumar(filas, "isr"),
  neto: sumar(filas, "neto"),
});

const vacias = (n) => Array.from({ length: n }, () => ({}));

/**
 * Las columnas del detalle. Cada una dice su ancho, cómo pinta su celda y, si suma, qué total
 * lleva en el subtotal. Encabezado, renglones y subtotales salen de esta MISMA lista.
 */
const columnasDetalle = (conFirma) => [
  { titulo: "No.", ancho: 24, celda: (f) => ({ text: codigoTxt(f.codigo), style: "celda" }) },
  {
    titulo: "Empleado",
    ancho: "*",
    alineacion: "left",
    celda: (f) => ({ text: f.nombre || "", style: "celdaFuerte" }),
  },
  { titulo: "Días aguin.", ancho: 30, celda: (f) => ({ text: String(f.diasAguinaldo ?? ""), style: "celdaCentro" }) },
  {
    titulo: "Días trab.",
    ancho: 42,
    // Los días del ejercicio y las incidencias descontadas, para que se vea de dónde salen.
    celda: (f) => ({
      text: [
        { text: String(f.diasTrabajados ?? "") },
        ...(f.diasIncidencias > 0 ? [{ text: `  (${f.diasCalendario}−${f.diasIncidencias})`, style: "celdaMini" }] : []),
      ],
      style: "celdaCentro",
    }),
  },
  {
    titulo: "Días a pagar",
    ancho: 40,
    celda: (f) => ({ text: fmtDias(f.diasProporcionales), style: "celdaCentroFuerte" }),
    total: (t) => fmtDias(t.diasProporcionales),
    estiloTotal: "subtotalCentro",
  },
  { titulo: "Salario diario", ancho: 46, celda: (f) => ({ text: fmtDinero(f.salarioDiario), style: "celdaNum" }) },
  {
    titulo: "Aguinaldo",
    ancho: 54,
    celda: (f) => ({ text: `${fmtDinero(f.importe)}${f.importeManual ? "*" : ""}`, style: "celdaNumFuerte" }),
    total: (t) => fmtDinero(t.importe),
  },
  { titulo: "Exento", ancho: 50, celda: (f) => ({ text: fmtDinero(f.exento), style: "celdaNum" }), total: (t) => fmtDinero(t.exento) },
  { titulo: "Gravado", ancho: 50, celda: (f) => ({ text: fmtDinero(f.gravado), style: "celdaNum" }), total: (t) => fmtDinero(t.gravado) },
  { titulo: "ISR", ancho: 48, celda: (f) => ({ text: fmtDinero(f.isr), style: "celdaNum" }), total: (t) => fmtDinero(t.isr) },
  {
    titulo: "Neto",
    ancho: 54,
    celda: (f) => ({ text: fmtDinero(f.neto), style: "celdaNumFuerte" }),
    total: (t) => fmtDinero(t.neto),
  },
  ...(conFirma ? [{ titulo: "Firma", ancho: 96, celda: () => ({ text: "" }) }] : []),
];

const layoutDetalle = {
  hLineWidth: (i) => (i === 0 || i === 1 ? 0 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => C_BORDE,
  paddingTop: () => 2,
  paddingBottom: () => 1.5,
  paddingLeft: () => 3,
  paddingRight: () => 3,
};

function tablaGrupo(grupo, columnas, conFirma, esPrimero) {
  const filas = grupo.filas || [];
  const t = totalesDe(filas);

  const encabezado = columnas.map((c) => ({ text: c.titulo, style: "th", alignment: c.alineacion || "center" }));

  const renglones = filas.map((f, i) => {
    const fill = i % 2 === 1 ? C_ZEBRA : null;
    return columnas.map((c) => ({ ...c.celda(f), fillColor: fill }));
  });
  if (!renglones.length) {
    renglones.push([{ text: "Sin empleados", style: "celdaTenue", colSpan: columnas.length }, ...vacias(columnas.length - 1)]);
  }

  // Subtotal: la etiqueta abarca las columnas que no suman, hasta la primera que sí.
  const primeraConTotal = columnas.findIndex((c) => c.total);
  const subtotal = [
    { text: `Subtotal · ${t.empleados} empleado(s)`, style: "subtotal", colSpan: primeraConTotal, fillColor: C_BANDA_GR },
    ...vacias(primeraConTotal - 1),
    ...columnas.slice(primeraConTotal).map((c) => ({
      text: c.total ? c.total(t) : "",
      style: c.estiloTotal || "subtotalNum",
      fillColor: C_BANDA_GR,
    })),
  ];

  return [
    { text: grupo.titulo || "", style: "grupo", margin: [0, esPrimero ? 0 : 12, 0, 3] },
    {
      table: {
        headerRows: 1,
        dontBreakRows: true,
        widths: columnas.map((c) => c.ancho),
        // Con firma, alto suficiente para firmar; sin ella, el renglón mide lo que su texto.
        ...(conFirma ? { heights: (fila) => (fila === 0 ? "auto" : 20) } : {}),
        body: [encabezado, ...renglones, subtotal],
      },
      layout: layoutDetalle,
    },
  ];
}

// ─── Hoja de totales ─────────────────────────────────────────────────────────

function hojaDeTotales({ empresa, proceso, aplicado, calculadoEn, isrIncompleto, grupos }) {
  const porGrupo = (grupos || []).map((g) => ({ titulo: g.titulo || "", ...totalesDe(g.filas || []) }));
  const todas = (grupos || []).flatMap((g) => g.filas || []);
  const general = totalesDe(todas);
  const manuales = todas.filter((f) => f.importeManual).length;
  const sinIsr = todas.filter((f) => f.isr === null || f.isr === undefined).length;
  const conIncidencias = todas.filter((f) => f.diasIncidencias > 0).length;

  const dato = (etiqueta, valor) => [
    { text: etiqueta, style: "datoEtiqueta" },
    { text: valor === undefined || valor === null || valor === "" ? "—" : String(valor), style: "datoValor" },
  ];

  const datos = {
    table: {
      widths: [150, "*"],
      body: [
        dato("Empresa", [empresa?.nombre, empresa?.rfc].filter(Boolean).join(" · ")),
        dato("Ejercicio", proceso.ejercicio),
        dato("Fecha de corte", fechaTxt(proceso.fechaCorte)),
        dato("Divisor del año", `${proceso.diasAnio || 365} días`),
        dato("Fórmula", `Días a pagar = días de aguinaldo × días trabajados ÷ ${proceso.diasAnio || 365}; aguinaldo = días a pagar × salario diario`),
        dato("Tipo de aplicación", proceso.tipoAplicacion === "extraordinaria" ? "Periodo extraordinario" : "Prenómina ordinaria"),
        dato("Destino", proceso.destino),
        ...(proceso.tipoAplicacion === "extraordinaria" && proceso.fechaPagoExtraordinaria
          ? [dato("Fecha de pago", fechaTxt(proceso.fechaPagoExtraordinaria))]
          : []),
        dato("Incidencias descontadas", proceso.incidencias || "Ninguna"),
        dato("Estatus", aplicado
          ? `Aplicado${proceso.fechaAplicacion ? ` el ${moment(proceso.fechaAplicacion).format("DD/MM/YYYY HH:mm")}` : ""}`
          : "Borrador (sin aplicar)"),
        ...(!aplicado
          ? [dato("Cálculo del ISR", calculadoEn ? `Simulado el ${moment(calculadoEn).format("DD/MM/YYYY HH:mm")}` : "Sin calcular")]
          : []),
      ],
    },
    layout: {
      hLineWidth: () => 0.4,
      vLineWidth: () => 0,
      hLineColor: () => C_BORDE,
      paddingTop: () => 3,
      paddingBottom: () => 3,
      paddingLeft: () => 4,
      paddingRight: () => 4,
    },
  };

  const th = (t, alignment = "right") => ({ text: t, style: "th", alignment });
  const cuerpo = [
    [th("Destino", "left"), th("Empleados", "center"), th("Días a pagar", "center"), th("Aguinaldo"), th("Exento"), th("Gravado"), th("ISR"), th("Neto")],
    ...porGrupo.map((g, i) => {
      const fill = i % 2 === 1 ? C_ZEBRA : null;
      return [
        { text: g.titulo, style: "celdaFuerte", fillColor: fill },
        { text: String(g.empleados), style: "celdaCentro", fillColor: fill },
        { text: fmtDias(g.diasProporcionales), style: "celdaCentro", fillColor: fill },
        { text: fmtDinero(g.importe), style: "celdaNum", fillColor: fill },
        { text: fmtDinero(g.exento), style: "celdaNum", fillColor: fill },
        { text: fmtDinero(g.gravado), style: "celdaNum", fillColor: fill },
        { text: fmtDinero(g.isr), style: "celdaNum", fillColor: fill },
        { text: fmtDinero(g.neto), style: "celdaNumFuerte", fillColor: fill },
      ];
    }),
    [
      { text: "TOTAL GENERAL", style: "totalLbl", fillColor: C_BANDA_GR },
      { text: String(general.empleados), style: "totalCentro", fillColor: C_BANDA_GR },
      { text: fmtDias(general.diasProporcionales), style: "totalCentro", fillColor: C_BANDA_GR },
      { text: fmtDinero(general.importe), style: "totalNum", fillColor: C_BANDA_GR },
      { text: fmtDinero(general.exento), style: "totalNum", fillColor: C_BANDA_GR },
      { text: fmtDinero(general.gravado), style: "totalNum", fillColor: C_BANDA_GR },
      { text: fmtDinero(general.isr), style: "totalNum", fillColor: C_BANDA_GR },
      { text: fmtDinero(general.neto), style: "totalNum", fillColor: C_BANDA_GR },
    ],
  ];

  const tablaTotales = {
    margin: [0, 14, 0, 0],
    table: { headerRows: 1, widths: ["*", 60, 64, 76, 72, 72, 68, 76], body: cuerpo },
    layout: {
      hLineWidth: (i, node) => (i === node.table.body.length - 1 || i === node.table.body.length ? 0.8 : i <= 1 ? 0 : 0.4),
      vLineWidth: () => 0,
      hLineColor: (i, node) => (i >= node.table.body.length - 1 ? C_TEAL_PRO : C_BORDE),
      paddingTop: () => 4,
      paddingBottom: () => 4,
      paddingLeft: () => 5,
      paddingRight: () => 5,
    },
  };

  const notas = [
    conIncidencias && `${conIncidencias} empleado(s) con incidencias descontadas de sus días trabajados.`,
    manuales && `${manuales} empleado(s) con importe capturado a mano (marcados con * en el detalle).`,
    !aplicado && isrIncompleto && sinIsr && `${sinIsr} empleado(s) sin ISR calculado: sus importes de ISR y neto no entran a estos totales.`,
  ].filter(Boolean);

  const firma = (titulo) => ({
    stack: [
      { canvas: [{ type: "line", x1: 0, y1: 0, x2: 180, y2: 0, lineWidth: 0.6, lineColor: "#595959" }], margin: [0, 46, 0, 4] },
      { text: titulo, style: "firmaTitulo" },
      { text: "Nombre y firma", style: "firmaSub" },
    ],
    alignment: "center",
    width: "*",
  });

  return [
    { text: "", pageBreak: "before" },
    {
      columns: [
        {
          width: "*",
          stack: [
            { text: "BIWO", style: "biwo" },
            { text: "NÓMINAS", style: "nominas" },
            { text: `Hoja de totales · Aguinaldo ${proceso.ejercicio}`, style: "tituloTotales", margin: [0, 6, 0, 0] },
          ],
        },
        { width: "auto", text: aplicado ? "APLICADO" : "BORRADOR", style: aplicado ? "sello" : "selloBorrador" },
      ],
    },
    { canvas: [{ type: "rect", x: 0, y: 0, w: ANCHO_UTIL, h: 3, color: C_TURQUESA }], margin: [0, 8, 0, 10] },
    datos,
    tablaTotales,
    ...(notas.length ? [{ ul: notas, style: "aviso", margin: [0, 10, 0, 0] }] : []),
    {
      columns: [firma("Elaboró"), firma("Revisó"), firma("Autorizó")],
      columnGap: 30,
      margin: [40, 20, 40, 0],
      unbreakable: true,
    },
  ];
}

// ─── Documento ───────────────────────────────────────────────────────────────

const borradorAguinaldoPdf = (datos, { conFirma = false } = {}) => {
  const { empresa, proceso, aplicado, calculadoEn, isrIncompleto, grupos } = datos;
  const totalEmpleados = (grupos || []).reduce((acc, g) => acc + (g.filas || []).length, 0);

  const meta = [
    `Corte al ${fechaTxt(proceso.fechaCorte)}`,
    proceso.tipoAplicacion === "extraordinaria" ? "Periodo extraordinario" : "Prenómina ordinaria",
    proceso.incidencias ? `Incidencias descontadas: ${proceso.incidencias}` : "Sin descontar incidencias",
  ].join("  ·  ");

  const encabezado = {
    columns: [
      {
        width: "*",
        stack: [
          { text: "BIWO", style: "biwo" },
          { text: "NÓMINAS", style: "nominas" },
          { text: empresa?.nombre || "", style: "empresa", margin: [0, 4, 0, 0] },
          { text: `Aguinaldo ${proceso.ejercicio}${empresa?.rfc ? `  ·  ${empresa.rfc}` : ""}`, style: "titulo" },
          { text: meta, style: "meta" },
          { text: `Destino: ${proceso.destino || ""}`, style: "meta" },
          {
            text: `Días a pagar = días de aguinaldo × días trabajados ÷ ${proceso.diasAnio || 365} días del año`
              + "   ·   Aguinaldo = días a pagar × salario diario",
            style: "formula",
          },
        ],
      },
      {
        width: "auto",
        stack: [
          { text: aplicado ? "APLICADO" : "BORRADOR", style: aplicado ? "sello" : "selloBorrador", alignment: "right" },
          { text: `${totalEmpleados} empleado(s)`, style: "meta", alignment: "right", margin: [0, 4, 0, 0] },
          ...(aplicado && proceso.fechaAplicacion
            ? [{ text: `Aplicado el ${moment(proceso.fechaAplicacion).format("DD/MM/YYYY HH:mm")}`, style: "meta", alignment: "right" }]
            : []),
          ...(!aplicado && calculadoEn
            ? [{ text: `ISR calculado el ${moment(calculadoEn).format("DD/MM/YYYY HH:mm")}`, style: "meta", alignment: "right" }]
            : []),
        ],
      },
    ],
  };

  const regla = {
    canvas: [{ type: "rect", x: 0, y: 0, w: ANCHO_UTIL, h: 3, color: C_TURQUESA }],
    margin: [0, 6, 0, 8],
  };

  const columnas = columnasDetalle(conFirma);
  const detalle = (grupos || []).length
    ? grupos.flatMap((g, i) => tablaGrupo(g, columnas, conFirma, i === 0))
    : [{ text: "No hay empleados incluidos en este proceso.", style: "aviso" }];

  const avisos = [];
  if ((grupos || []).some((g) => (g.filas || []).some((f) => f.importeManual))) {
    avisos.push({ text: "* Importe capturado a mano: no sale de la fórmula de días a pagar × salario diario.", style: "aviso", margin: [0, 8, 0, 0] });
  }
  if (isrIncompleto) {
    avisos.push({
      text: "Hay empleados sin ISR calculado (—): el importe cambió después del cálculo o no se ha calculado. "
        + "Usa \"Calcular ISR\" en el resumen del asistente antes de tomar estos números como definitivos.",
      style: "aviso",
      margin: [0, 4, 0, 0],
    });
  }
  avisos.push({
    text: aplicado
      ? "Importes tomados de los recibos de la prenómina. El ISR corresponde al concepto I.S.R. Art. 174 del recibo."
      : "Borrador: el ISR es una simulación del motor de nómina con las tablas vigentes; se confirma al calcular la prenómina.",
    style: "aviso",
    margin: [0, 4, 0, 0],
  });

  return {
    pageSize: "LETTER",
    pageOrientation: "landscape",
    pageMargins: [28, 24, 28, 30],
    ...(aplicado ? {} : { watermark: { text: "BORRADOR", color: C_NARANJA, opacity: 0.06, bold: true } }),
    footer: (pagina, total) => ({
      columns: [
        { text: `Aguinaldo ${proceso.ejercicio} · Generado ${moment().format("DD/MM/YYYY HH:mm")}`, style: "pie" },
        { text: `${pagina} / ${total}`, style: "pie", alignment: "right" },
      ],
      margin: [28, 8, 28, 0],
    }),
    content: [
      encabezado,
      regla,
      ...detalle,
      ...avisos,
      ...hojaDeTotales({ empresa, proceso, aplicado, calculadoEn, isrIncompleto, grupos }),
    ],
    styles: {
      biwo: { fontSize: 18, bold: true, color: C_TURQUESA },
      nominas: { fontSize: 7.5, bold: true, color: C_NARANJA },
      empresa: { fontSize: 12, bold: true },
      titulo: { fontSize: 10, bold: true, color: C_TEAL_PRO, margin: [0, 2, 0, 1] },
      tituloTotales: { fontSize: 14, bold: true, color: C_TEAL_PRO },
      meta: { fontSize: 7.5, color: C_TINTA_SU },
      formula: { fontSize: 7.5, color: C_TEAL_PRO, margin: [0, 2, 0, 0] },
      sello: { fontSize: 15, bold: true, color: C_TEAL_PRO },
      selloBorrador: { fontSize: 15, bold: true, color: C_NARANJA },
      grupo: { fontSize: 8.5, bold: true, color: C_TEAL_PRO },
      th: { fontSize: 6.8, bold: true, color: "#FFFFFF", fillColor: C_TURQUESA, margin: [0, 1, 0, 1] },
      celda: { fontSize: 6.8 },
      celdaCentro: { fontSize: 6.8, alignment: "center" },
      celdaCentroFuerte: { fontSize: 6.8, bold: true, alignment: "center" },
      celdaMini: { fontSize: 5.5, color: C_TINTA_SU },
      celdaFuerte: { fontSize: 6.8, bold: true },
      celdaTenue: { fontSize: 6.8, color: C_TINTA_SU, italics: true },
      celdaNum: { fontSize: 6.8, alignment: "right" },
      celdaNumFuerte: { fontSize: 6.8, bold: true, alignment: "right" },
      subtotal: { fontSize: 7.2, bold: true, color: C_TEAL_PRO },
      subtotalNum: { fontSize: 7.2, bold: true, color: C_TEAL_PRO, alignment: "right" },
      subtotalCentro: { fontSize: 7.2, bold: true, color: C_TEAL_PRO, alignment: "center" },
      datoEtiqueta: { fontSize: 8, bold: true, color: "#595959" },
      datoValor: { fontSize: 8 },
      totalLbl: { fontSize: 9, bold: true, color: C_TEAL_PRO },
      totalNum: { fontSize: 9, bold: true, color: C_TEAL_PRO, alignment: "right" },
      totalCentro: { fontSize: 9, bold: true, color: C_TEAL_PRO, alignment: "center" },
      firmaTitulo: { fontSize: 8.5, bold: true, color: C_TEAL_PRO },
      firmaSub: { fontSize: 7, color: C_TINTA_SU },
      aviso: { fontSize: 7, italics: true, color: C_TINTA_SU },
      pie: { fontSize: 6.5, color: C_TINTA_SU },
    },
  };
};

export default borradorAguinaldoPdf;
